import React, { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  Typography,
  Button,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  InputAdornment,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  CircularProgress,
  Alert,
  Chip,
  Tooltip,
  Switch,
  FormControlLabel,
  Divider,
  Card,
  CardContent,
  Menu,
  MenuItem,
} from '@mui/material';
import {
  Add as AddIcon,
  Search as SearchIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  MoreVert as MoreVertIcon,
  Refresh as RefreshIcon,
  AccountBalance as AccountBalanceIcon,
  People as PeopleIcon,
  Payment as PaymentIcon,
  Visibility as VisibilityIcon
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import * as paymentMethodService from '../../../services/paymentMethodService';
import PaymentMethodForm from './PaymentMethodForm';
import { PaymentMethod } from '../../../types/paymentMethod';

const PaymentMethodsList: React.FC = () => {
  const navigate = useNavigate();
  const [paymentMethods, setPaymentMethods] = useState<PaymentMethod[]>([]);
  const [filteredMethods, setFilteredMethods] = useState<PaymentMethod[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState<string>('');
  const [showAddForm, setShowAddForm] = useState<boolean>(false);
  const [editingMethod, setEditingMethod] = useState<PaymentMethod | null>(null);
  const [refreshTrigger, setRefreshTrigger] = useState<number>(0);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState<boolean>(false);
  const [methodToDelete, setMethodToDelete] = useState<PaymentMethod | null>(null);
  
  // Anchor for menu
  const [menuAnchorEl, setMenuAnchorEl] = useState<null | HTMLElement>(null);
  const [selectedMethodId, setSelectedMethodId] = useState<string | null>(null);
  
  // الحصول على طرق الدفع عند تحميل الصفحة
  useEffect(() => {
    const fetchPaymentMethods = async () => {
      try {
        setLoading(true);
        const methods = await paymentMethodService.getAllPaymentMethods();
        
        // تحويل البيانات من شكل الخدمة إلى شكل الواجهة المطلوبة
        const transformedMethods: PaymentMethod[] = methods.map(method => ({
          id: method.id || '',
          name: method.name,
          fixedFees: method.fixedFees || 0,
          variableFees: method.variableFees || 0,
          taxPercentage: method.taxPercentage || 0,
          linkedAccountId: typeof method.linkedAccount === 'object' ?
                           (method.linkedAccount._id || method.linkedAccount.id) :
                           (method.linkedAccountId || (method.linkedAccount as string) || ''),
          linkedAccount: method.linkedAccount, // حفظ كائن الحساب المرتبط كاملاً
          linkedAccountName: typeof method.linkedAccount === 'object' ? method.linkedAccount.name : method.linkedAccountName,
          linkedServiceProductId: method.linkedServiceProductId,
          linkedServiceProductName: method.linkedServiceProductName,
          authorizedUserIds: (method.authorizedUsers?.map(user =>
                              typeof user === 'object' ? (user._id || user.id) : user) || []) as string[],
          authorizedUsers: method.authorizedUsers, // حفظ قائمة المستخدمين المصرح لهم كاملة
          authorizedUserNames: method.authorizedUsers?.map(user =>
                               typeof user === 'object' ? (user.name || user.fullName || user.username) : '') || [],
          isActive: method.isActive
        }));
        
        setPaymentMethods(transformedMethods);
        setFilteredMethods(transformedMethods);
        setError(null);
      } catch (err) {
        console.error('Error fetching payment methods:', err);
        setError('حدث خطأ أثناء جلب طرق الدفع');
      } finally {
        setLoading(false);
      }
    };
    
    fetchPaymentMethods();
  }, [refreshTrigger]);
  
  // البحث
  useEffect(() => {
    if (searchTerm.trim() === '') {
      setFilteredMethods(paymentMethods);
      return;
    }
    
    const lowercasedSearch = searchTerm.toLowerCase();
    const filtered = paymentMethods.filter(method =>
      method.name.toLowerCase().includes(lowercasedSearch) ||
      (method.linkedAccountName && method.linkedAccountName.toLowerCase().includes(lowercasedSearch)) ||
      (method.linkedServiceProductName && method.linkedServiceProductName.toLowerCase().includes(lowercasedSearch))
    );
    
    setFilteredMethods(filtered);
  }, [searchTerm, paymentMethods]);
  
  // إضافة طريقة دفع جديدة
  const handleAddMethodSuccess = () => {
    setShowAddForm(false);
    setRefreshTrigger(prev => prev + 1);
  };
  
  // تعديل طريقة دفع
  const handleEditMethod = (method: PaymentMethod) => {
    setEditingMethod(method);
    setMenuAnchorEl(null);
  };
  
  // إقفال نموذج التعديل
  const handleEditMethodClose = () => {
    setEditingMethod(null);
  };
  
  // نجاح تعديل طريقة الدفع
  const handleEditMethodSuccess = () => {
    setEditingMethod(null);
    setRefreshTrigger(prev => prev + 1);
  };
  
  // حذف طريقة دفع
  const handleDeletePrompt = (method: PaymentMethod) => {
    setMethodToDelete(method);
    setDeleteDialogOpen(true);
    setMenuAnchorEl(null);
  };
  
  // تأكيد الحذف
  const handleConfirmDelete = async () => {
    if (!methodToDelete) return;
    
    try {
      setLoading(true);
      await paymentMethodService.deletePaymentMethod(methodToDelete.id);
      setDeleteDialogOpen(false);
      setMethodToDelete(null);
      setRefreshTrigger(prev => prev + 1);
    } catch (err) {
      console.error('Error deleting payment method:', err);
      setError('حدث خطأ أثناء حذف طريقة الدفع');
    } finally {
      setLoading(false);
    }
  };
  
  // تغيير حالة طريقة الدفع (تفعيل/تعطيل)
  const handleToggleStatus = async (method: PaymentMethod) => {
    try {
      setLoading(true);
      await paymentMethodService.togglePaymentMethodStatus(method.id, !method.isActive);
      setRefreshTrigger(prev => prev + 1);
      setMenuAnchorEl(null);
    } catch (err) {
      console.error('Error toggling payment method status:', err);
      setError('حدث خطأ أثناء تغيير حالة طريقة الدفع');
    } finally {
      setLoading(false);
    }
  };
  
  // فتح القائمة
  const handleMenuOpen = (event: React.MouseEvent<HTMLElement>, methodId: string) => {
    setMenuAnchorEl(event.currentTarget);
    setSelectedMethodId(methodId);
  };
  
  // إغلاق القائمة
  const handleMenuClose = () => {
    setMenuAnchorEl(null);
    setSelectedMethodId(null);
  };
  
  return (
    <Box sx={{ maxWidth: '100%', p: { xs: 2, sm: 3 } }}>
      <Card elevation={3} sx={{ borderRadius: 2, overflow: 'visible' }}>
        <CardContent sx={{ p: { xs: 2, sm: 3 } }}>
          {/* رأس الصفحة */}
          <Box display="flex" justifyContent="space-between" alignItems="center" mb={3} flexWrap="wrap" gap={2}>
            <Typography variant="h5" component="h1" sx={{ 
              display: 'flex', 
              alignItems: 'center', 
              gap: 1,
              color: 'primary.main',
              fontWeight: 500 
            }}>
              <PaymentIcon />
              طرق الدفع
            </Typography>
            
            <Box display="flex" gap={1}>
              <Button
                variant="contained"
                color="primary"
                startIcon={<AddIcon />}
                onClick={() => setShowAddForm(true)}
                sx={{ borderRadius: 2 }}
              >
                إضافة طريقة دفع
              </Button>
              
              <IconButton 
                color="primary" 
                onClick={() => setRefreshTrigger(prev => prev + 1)}
                sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 1 }}
              >
                <RefreshIcon />
              </IconButton>
            </Box>
          </Box>
          
          {/* خط البحث */}
          <Box mb={3}>
            <TextField
              fullWidth
              variant="outlined"
              placeholder="البحث في طرق الدفع..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon />
                  </InputAdornment>
                ),
              }}
              size="small"
              sx={{ bgcolor: 'background.paper' }}
            />
          </Box>
          
          {/* رسالة الخطأ */}
          {error && (
            <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>
              {error}
            </Alert>
          )}
          
          {/* جدول طرق الدفع */}
          <TableContainer component={Paper} variant="outlined" sx={{ 
            borderRadius: 1,
            boxShadow: 'none',
            overflow: 'hidden'
          }}>
            <Table>
              <TableHead sx={{ bgcolor: 'grey.100' }}>
                <TableRow>
                  <TableCell sx={{ fontWeight: 'bold' }}>طريقة الدفع</TableCell>
                  <TableCell sx={{ fontWeight: 'bold' }}>الحساب المرتبط</TableCell>
                  <TableCell sx={{ fontWeight: 'bold' }}>المنتج الخدمي</TableCell>
                  <TableCell sx={{ fontWeight: 'bold' }}>الرسوم</TableCell>
                  <TableCell sx={{ fontWeight: 'bold' }}>المستخدمون المصرح لهم</TableCell>
                  <TableCell sx={{ fontWeight: 'bold' }}>الحالة</TableCell>
                  <TableCell align="center" sx={{ fontWeight: 'bold' }}>إجراءات</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {loading && paymentMethods.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={7} align="center" sx={{ py: 3 }}>
                      <CircularProgress size={30} />
                      <Typography variant="body1" sx={{ mt: 1 }}>
                        جاري تحميل البيانات...
                      </Typography>
                    </TableCell>
                  </TableRow>
                ) : filteredMethods.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={7} align="center" sx={{ py: 3 }}>
                      <Typography variant="body1">
                        {searchTerm ? 'لا توجد نتائج مطابقة للبحث' : 'لا توجد طرق دفع حتى الآن'}
                      </Typography>
                      {searchTerm && (
                        <Button
                          variant="text"
                          onClick={() => setSearchTerm('')}
                          sx={{ mt: 1 }}
                        >
                          عرض جميع طرق الدفع
                        </Button>
                      )}
                    </TableCell>
                  </TableRow>
                ) : (
                  filteredMethods.map((method) => (
                    <TableRow key={method.id} sx={{
                      '&:hover': { bgcolor: 'action.hover' },
                      backgroundColor: method.isActive ? 'inherit' : 'rgba(0, 0, 0, 0.04)'
                    }}>
                      <TableCell>
                        <Box display="flex" alignItems="center" gap={1}>
                          <PaymentIcon color="primary" fontSize="small" />
                          <Typography>{method.name}</Typography>
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Box display="flex" alignItems="center" gap={1}>
                          <AccountBalanceIcon color="secondary" fontSize="small" />
                          <Typography>{method.linkedAccountName || "غير محدد"}</Typography>
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2">
                          {method.linkedServiceProductName || '—'}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Tooltip
                          title={
                            <Box>
                              <Typography variant="body2">رسوم ثابتة: {method.fixedFees} ريال</Typography>
                              <Typography variant="body2">رسوم متغيرة: {method.variableFees}%</Typography>
                              <Typography variant="body2">الضريبة: {method.taxPercentage}%</Typography>
                            </Box>
                          }
                        >
                          <Box display="flex" flexDirection="column" gap={0.5}>
                            <Chip
                              label={`ثابتة: ${method.fixedFees} ريال`}
                              size="small"
                              variant="outlined"
                              color="primary"
                              sx={{ maxWidth: 150 }}
                            />
                            <Chip
                              label={`متغيرة: ${method.variableFees}%`}
                              size="small"
                              variant="outlined"
                              color="secondary"
                              sx={{ maxWidth: 150 }}
                            />
                          </Box>
                        </Tooltip>
                      </TableCell>
                      <TableCell>
                        <Box display="flex" alignItems="center" gap={1}>
                          <PeopleIcon color="info" fontSize="small" />
                          <Typography>
                            {method.authorizedUserNames?.length || method.authorizedUserIds?.length || 0} مستخدم
                          </Typography>
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={method.isActive ? 'مفعل' : 'معطل'}
                          color={method.isActive ? 'success' : 'default'}
                          size="small"
                        />
                      </TableCell>
                      <TableCell align="center">
                        <IconButton
                          size="small"
                          onClick={(e) => handleMenuOpen(e, method.id)}
                        >
                          <MoreVertIcon />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </CardContent>
      </Card>
      
      {/* قائمة الإجراءات */}
      <Menu
        anchorEl={menuAnchorEl}
        open={Boolean(menuAnchorEl)}
        onClose={handleMenuClose}
      >
        {selectedMethodId && (
          <>
            <MenuItem onClick={() => {
              const method = paymentMethods.find(m => m.id === selectedMethodId);
              if (method) handleEditMethod(method);
            }}>
              <EditIcon fontSize="small" sx={{ mr: 1 }} />
              تعديل
            </MenuItem>
            <MenuItem onClick={() => {
              const method = paymentMethods.find(m => m.id === selectedMethodId);
              if (method) handleToggleStatus(method);
            }}>
              <VisibilityIcon fontSize="small" sx={{ mr: 1 }} />
              {paymentMethods.find(m => m.id === selectedMethodId)?.isActive ? 'تعطيل' : 'تفعيل'}
            </MenuItem>
            <Divider />
            <MenuItem onClick={() => {
              const method = paymentMethods.find(m => m.id === selectedMethodId);
              if (method) handleDeletePrompt(method);
            }} sx={{ color: 'error.main' }}>
              <DeleteIcon fontSize="small" sx={{ mr: 1 }} />
              حذف
            </MenuItem>
          </>
        )}
      </Menu>
      
      {/* نموذج إضافة طريقة دفع جديدة */}
      <Dialog
        open={showAddForm}
        onClose={() => setShowAddForm(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          <Box display="flex" alignItems="center" gap={1}>
            <AddIcon color="primary" />
            <Typography variant="h6">إضافة طريقة دفع جديدة</Typography>
          </Box>
        </DialogTitle>
        <DialogContent>
          <PaymentMethodForm 
            onSuccess={handleAddMethodSuccess} 
            onCancel={() => setShowAddForm(false)} 
          />
        </DialogContent>
      </Dialog>
      
      {/* نموذج تعديل طريقة دفع */}
      <Dialog
        open={Boolean(editingMethod)}
        onClose={handleEditMethodClose}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          <Box display="flex" alignItems="center" gap={1}>
            <EditIcon color="primary" />
            <Typography variant="h6">تعديل طريقة الدفع</Typography>
          </Box>
        </DialogTitle>
        <DialogContent>
          {editingMethod && (
            <PaymentMethodForm 
              initialData={editingMethod} 
              onSuccess={handleEditMethodSuccess} 
              onCancel={handleEditMethodClose} 
            />
          )}
        </DialogContent>
      </Dialog>
      
      {/* تأكيد الحذف */}
      <Dialog
        open={deleteDialogOpen}
        onClose={() => setDeleteDialogOpen(false)}
      >
        <DialogTitle>تأكيد الحذف</DialogTitle>
        <DialogContent>
          <Typography>
            هل أنت متأكد من رغبتك في حذف طريقة الدفع "{methodToDelete?.name}"؟
          </Typography>
          <Typography color="error" sx={{ mt: 2 }}>
            لا يمكن التراجع عن هذه العملية.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)}>
            إلغاء
          </Button>
          <Button 
            onClick={handleConfirmDelete} 
            color="error" 
            variant="contained" 
            disabled={loading}
          >
            {loading ? <CircularProgress size={24} /> : 'حذف'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default PaymentMethodsList;